import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import type { useNavigationNotifications } from '../hooks/useNavigationNotifications';
import { EmptyState } from './EmptyState';

export function NavigationCount({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return <span aria-label={`${label}: ${count}`} title={`${label}: ${count}`} className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-danger px-1 text-xs font-semibold tabular-nums text-white">{count}</span>;
}

export function NavigationNotifications({ query }: { query: ReturnType<typeof useNavigationNotifications>['complaints'] }) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const count = query.data?.pagination.total ?? 0;
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return <div ref={container} className="relative flex">
    <button ref={trigger} type="button" aria-label={`Powiadomienia, nowe zgłoszenia: ${count}`} aria-expanded={open} aria-controls="navigation-notifications" onClick={() => { setOpen(value => !value); if (!open) void query.refetch(); }} className="relative px-1.5 flex items-center justify-center rounded-lg bg-surface-hover/30 border border-border-subtle text-text-secondary hover:text-text-primary hover:border-accent-primary/30 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-primary">
      <span className="w-7 h-7 flex items-center justify-center"><Bell className="w-4 h-4" /></span>
      {count > 0 && <span aria-hidden className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-danger rounded-full" />}
    </button>
    {open && <section id="navigation-notifications" aria-label="Powiadomienia o nowych zgłoszeniach" className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-xl bg-surface-raised border border-border shadow-xl">
      <div className="p-4 border-b border-border-subtle"><h2 className="font-semibold text-sm text-text-primary">Nowe zgłoszenia {count > 0 && `(${count})`}</h2><p className="text-xs text-text-muted mt-1">Zwroty, reklamacje i wymiany</p></div>
      {query.isPending ? <div className="p-4 text-sm text-text-muted" role="status">Ładowanie powiadomień...</div>
        : query.isError ? <div className="p-4 text-sm text-text-secondary"><p>Nie udało się odświeżyć powiadomień.</p><button type="button" className="btn-secondary mt-3" onClick={() => void query.refetch()}>Ponów</button></div>
        : count === 0 ? <EmptyState size="sm" icon={Bell} title="Brak nowych zgłoszeń" />
        : <ul>{query.data?.complaints.map(complaint => <li key={complaint.id}>
          <Link onClick={() => setOpen(false)} to={`/complaints?status=SUBMITTED&complaintId=${encodeURIComponent(complaint.id)}`} className="block p-4 hover:bg-surface-hover border-b border-border-subtle focus-visible:outline focus-visible:outline-accent-primary">
            <p className="text-sm font-medium text-text-primary">{({ RETURN: 'Zwrot', COMPLAINT: 'Reklamacja', EXCHANGE: 'Wymiana' })[complaint.type]} · #{complaint.order.orderNumber}</p>
            <p className="text-xs text-text-muted mt-1">{complaint.order.store.name} · {new Date(complaint.createdAt).toLocaleString('pl-PL')}</p>
          </Link>
        </li>)}</ul>}
      <Link to="/complaints?status=SUBMITTED" onClick={() => setOpen(false)} className="block p-4 text-sm font-medium text-accent-primary hover:bg-surface-hover">Wszystkie nowe zgłoszenia</Link>
    </section>}
  </div>;
}
